import { useState, useCallback, useEffect } from "react";
import type { ReactNode } from "react";
import {
  Dialog,
  DialogSurface,
} from "@fluentui/react-components";
import type { IPartnerInfo } from "@/types/partnerTypes";
import { partnerService } from "@/services/partnerService";
import { dispatchRequestCreated } from "@/constants/requestEvents";
import { DialogHeader } from "./DialogHeader";
import { PartnerLookup } from "./PartnerLookup";
import { ManagerSelection } from "./ManagerSelection";
import { DialogFooter } from "./DialogFooter";
import styles from "./NewRequestDialog.module.scss";

interface INewRequestDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onRequestCreated?: () => void;
}

export function NewRequestDialog({ isOpen, onClose, onRequestCreated }: INewRequestDialogProps): ReactNode {
  const [partner, setPartner] = useState<IPartnerInfo | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedManagerId, setSelectedManagerId] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const needsManager = partner?.isEligible === false;
  const isSubmitDisabled =
    !partner ||
    searchError !== null ||
    (needsManager && selectedManagerId === "") ||
    isSubmitting;

  // Reset form state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setPartner(null);
      setSearchError(null);
      setSelectedManagerId("");
      setIsSubmitting(false);
      setSubmitError(null);
    }
  }, [isOpen]);

  const handlePartnerFound = useCallback((result: IPartnerInfo): void => {
    setPartner(result);
    setSearchError(null);
    setSelectedManagerId("");
    setSubmitError(null);
  }, []);

  const handlePartnerCleared = useCallback((): void => {
    setPartner(null);
    setSearchError(null);
    setSelectedManagerId("");
    setSubmitError(null);
  }, []);

  const handleSearchError = useCallback((message: string): void => {
    setPartner(null);
    setSearchError(message);
    setSelectedManagerId("");
    setSubmitError(null);
  }, []);

  const handleSubmit = useCallback(async (): Promise<void> => {
    if (isSubmitDisabled || !partner) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const selectedPdmEmail = needsManager ? selectedManagerId : undefined;
      const selectedPdm = needsManager
        ? partner.associatedPdms.find((pdm) => pdm.email === selectedManagerId)
        : undefined;
      const managerName = selectedPdm?.displayName ?? undefined;

      const response = await partnerService.createRequest({
        partnerOneId: partner.partnerOneId,
        partnerName: partner.partnerName,
        managerEmail: selectedPdmEmail,
        managerName,
      });

      if (response.warning) {
        console.warn("Request warning:", response.warning);
      }

      onRequestCreated?.();
      dispatchRequestCreated();
      onClose();
    } catch {
      setSubmitError("Failed to submit request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }, [partner, isSubmitDisabled, needsManager, selectedManagerId, onRequestCreated, onClose]);

  const submitLabel = needsManager ? "Submit for manager approval" : "Submit new request";
  const submitText = isSubmitting ? "Submitting..." : "Submit";

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(_event, data) => { if (!data.open) onClose(); }}
    >
      <DialogSurface className={styles.dialog} aria-labelledby="newRequestDialogTitle">
        <div className={styles.dialogContent}>
          <DialogHeader title="Request download" titleId="newRequestDialogTitle" onClose={onClose} />

          <PartnerLookup
            partner={partner}
            searchError={searchError}
            onPartnerFound={handlePartnerFound}
            onPartnerCleared={handlePartnerCleared}
            onSearchError={handleSearchError}
          />

          {partner && !searchError && needsManager && (
            <ManagerSelection
              partner={partner}
              pdms={partner.associatedPdms}
              selectedPdmEmail={selectedManagerId}
              onPdmChange={setSelectedManagerId}
            />
          )}

          {submitError && (
            <div className={styles.statusIndicator} role="alert">
              <span className={styles.statusText}>{submitError}</span>
            </div>
          )}

          <DialogFooter
            onCancel={onClose}
            onSubmit={handleSubmit}
            isSubmitDisabled={isSubmitDisabled}
            submitLabel={submitLabel}
            submitText={submitText}
          />
        </div>
      </DialogSurface>
    </Dialog>
  );
}
